import { type Language } from '@ifrc-go/ui/contexts';
import {
    DEFAULT_INVALID_TEXT,
    getWordCount,
} from '@ifrc-go/ui/utils';
import {
    isNotDefined,
    isTruthyString,
} from '@togglecorp/fujs';

import type { GoApiResponse } from '#utils/restRequest';

type SearchResponse = GoApiResponse<'/api/v1/search/'>;

type SearchResponseKeys = keyof SearchResponse;

export const defaultRanking: Record<SearchResponseKeys, number> = {
    regions: 1,
    countries: 2,
    district_province_response: 3,

    emergencies: 4,
    projects: 5,
    surge_alerts: 6,
    surge_deployments: 7,
    reports: 8,
    rapid_response_deployments: 9,
};

export function downloadFile(
    blob: Blob,
    filename: string,
    fileExtension: string,
): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.${fileExtension}`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
}

// NOTE: these doesn't need to be translated
export const languageNameMap: Record<Language, string> = {
    en: 'English',
    fr: 'Français',
    es: 'Español',
    ar: 'عربي',
};

export function getFirstTruthyString(
    primaryStr: string | null | undefined,
    secondaryStr: string | null | undefined,
    invalidText = DEFAULT_INVALID_TEXT,
) {
    if (isTruthyString(primaryStr)) {
        return primaryStr;
    }

    if (isTruthyString(secondaryStr)) {
        return secondaryStr;
    }

    return invalidText;
}

export function joinStrings(
    values: (string | undefined)[],
    separator: string = ', ',
): string {
    return values.filter(Boolean).join(separator);
}

const imageFileExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'avif'];

// NOTE: not using `new URL` here as it throws for the relative urls
export function getFileNameFromUrl(urlString: string | undefined | null) {
    if (isNotDefined(urlString)) {
        return undefined;
    }

    const [pathname] = urlString.split(/[?#]/);
    return pathname?.split('/').pop();
}

export function isImageFile(urlString: string | undefined | null) {
    const fileName = getFileNameFromUrl(urlString);

    if (isNotDefined(fileName)) {
        return false;
    }

    const extension = fileName.split('.').pop()?.toLowerCase();
    return isTruthyString(extension) && imageFileExtensions.includes(extension);
}

export function isFileAccepted(file: File, accept: string | undefined | null) {
    if (isNotDefined(accept)) {
        return true;
    }

    const acceptedFormats = accept
        .split(',')
        .map((format) => format.trim().toLowerCase())
        .filter(isTruthyString);

    if (acceptedFormats.length === 0
        || acceptedFormats.includes('*')
        || acceptedFormats.includes('*/*')
    ) {
        return true;
    }

    const fileName = file.name.toLowerCase();
    const fileType = file.type.toLowerCase();

    return acceptedFormats.some((format) => {
        if (format.startsWith('.')) {
            return fileName.endsWith(format);
        }

        if (format.endsWith('/*')) {
            return fileType.startsWith(format.substring(0, format.length - 1));
        }

        return fileType === format;
    });
}

export function formatSourceLink(value: string | undefined): string | undefined {
    if (
        isNotDefined(value)
            || value.startsWith('http://')
            || value.startsWith('https://')
            || value === 'h'
            || value === 'ht'
            || value === 'htt'
            || value === 'http'
            || value === 'http:'
            || value === 'http:/'
            || value === 'https'
            || value === 'https:'
            || value === 'https:/'
    ) {
        return value;
    }

    return `https://${value}`;
}

export function lengthSmallerOrEqualToCondition(
    x?: number,
    type: 'word' | 'character' = 'word',
) {
    return (value: string | undefined) => {
        if (isNotDefined(value) || isNotDefined(x)) {
            return undefined;
        }

        const length = type === 'word' ? getWordCount(value) : value.length;

        if (length <= x) {
            return undefined;
        }

        // FIXME: use translations
        return type === 'word'
            ? `Must be smaller or equal to ${x} words`
            : `Length must be smaller or equal to ${x}`;
    };
}
